import * as PIXI from "pixi";
import data, { PiouPiouZLib } from "./piou-animate";

import mcDecorPosition from "../assets/mcDecor.json";
import mcTile1Position from "../assets/mcTile1.json";
import mcTile2Position from "../assets/mcTile2.json";

import { DeobfuPiouPiouzLevel } from "piou-core/types";
import Level from "./level/Level";
import { ARTWORK_MAX } from "./level/conf";

const RESOLUTION: 1 | 2 | 4 = 4;

export default class Loader<T> {
  app: PIXI.Application;

  dom: HTMLElement;
  lvlInfo: DeobfuPiouPiouzLevel;
  mode: new (
    app: PIXI.Application,
    lvlInfo: DeobfuPiouPiouzLevel,
    level: Level
  ) => T;

  private static loadedTextureKeys: Set<string> = new Set();

  static RESOLUTION = RESOLUTION;
  static texturesPosition: Record<string, PIXI.Point> = {};
  static textures: Record<string, PIXI.Texture> = {};
  static get movieclips(): PiouPiouZLib {
    return data.lib;
  }

  constructor(
    width: number,
    height: number,
    element: HTMLElement,
    level: DeobfuPiouPiouzLevel,
    mode: new (
      app: PIXI.Application,
      lvlInfo: DeobfuPiouPiouzLevel,
      level: Level
    ) => T
  ) {
    this.lvlInfo = level;
    this.mode = mode;
    this.dom = element;

    this.dom.innerHTML = "<p>LOADING...</p>"; // TODO

    this.app = new PIXI.animate.Scene({
      width: width,
      height: height,

      antialias: true,
      backgroundAlpha: 0,
      // forceCanvas: true, // Check often if it's still works fine without webgl
    });
  }

  async preloadTextures() {
    return new Promise<void>((resolve) => {
      const lvlData = this.lvlInfo.data;

      if (!data.isSetup) {
        data.setup({
          MovieClip: PIXI.animate.MovieClip,
          Container: PIXI.animate.Container,
          Graphics: CacheGraphics,
          Text: PIXI.animate.Text,
          Sprite: PIXI.animate.Sprite,
        });
        data.isSetup = true;
      }

      function loadIfNotLoaded(
        textureKey: string,
        url: string,
        spritePos?: number[]
      ) {
        if (!Loader.loadedTextureKeys.has(textureKey)) {
          Loader.loadedTextureKeys.add(textureKey);
          PIXI.Loader.shared.add(textureKey, url, (resource) => {
            if (resource.error) throw resource.error;
            Loader.textures[textureKey] = resource.texture!;
            if (spritePos)
              Loader.texturesPosition[textureKey] = new PIXI.Point(
                -spritePos[0],
                -spritePos[1]
              );
          });
        }
      }

      const resolutionSuffix = Loader.RESOLUTION === 1 ? "" : `@${RESOLUTION}x`;

      // TODO : sprite-sheets ?
      for (const tile of lvlData.tiles) {
        const tileName = `tile${lvlData.did + 1}${String(tile.id).padStart(
          4,
          "0"
        )}`;
        loadIfNotLoaded(
          tileName,
          `tiles${lvlData.did + 1}/${tileName}${resolutionSuffix}.png`,
          lvlData.did === 0
            ? mcTile1Position[tile.id]
            : mcTile2Position[tile.id]
        );
      }

      for (const tile of lvlData.platforms) {
        if (tile.rid !== undefined && tile.rid <= ARTWORK_MAX) {
          const tileName = `mcDecor${String(tile.rid).padStart(4, "0")}`;
          loadIfNotLoaded(
            tileName,
            // There is no need of a @4x version of the artwork
            `decor/${tileName}${Loader.RESOLUTION === 4 ? "@2x" : ""}.png`,
            mcDecorPosition[tile.rid]
          );
        }
      }

      loadIfNotLoaded(
        `mcCroute${lvlData.did + 1}`,
        `croute/mcCroute${lvlData.did + 1}${resolutionSuffix}.png`
      );
      loadIfNotLoaded("mcOut", `other/mcOut${resolutionSuffix}.png`, [43, 46]);
      loadIfNotLoaded(
        "mcCaisse",
        `other/mcCaisse${resolutionSuffix}.png`,
        [10, 17]
      );
      loadIfNotLoaded(
        `text${lvlData.did + 1}`,
        `text/text${lvlData.did + 1}${resolutionSuffix}.png`
      );
      // Draw with HTML
      // loadIfNotLoaded(`bg${lvlData.did + 1}`, `bg/bg${lvlData.did + 1}.webp`);

      PIXI.Loader.shared.load(() => {
        resolve();
      });
    });
  }

  generateLevel(app: PIXI.Application) {
    return new Level(app, this.lvlInfo.data);
  }

  async load(): Promise<T> {
    return new Promise<T>(async (resolve) => {
      await this.preloadTextures();

      PIXI.animate.load(data, {
        basePath: "./",
        parent: this.app.stage,
        createInstance: false, // Start with an empty scene
        complete: () => {
          const level = this.generateLevel(this.app);
          console.log("Loading complete");

          this.app.view.style.backgroundImage = `url('bg/bg${
            this.lvlInfo.data.did + 1
          }.webp')`;
          this.app.view.style.backgroundSize = "cover";
          this.app.view.style.backgroundRepeat = "no-repeat";
          this.app.view.style.backgroundPosition = "center center";
          this.dom.replaceChildren(this.app.view);

          resolve(new this.mode(this.app, this.lvlInfo, level));
        },
      });
    });
  }

  static getSprite(name: string) {
    const text = Loader.textures[name];
    const position = Loader.texturesPosition[name]?.clone() || new PIXI.Point();
    if (!text) throw new Error(`Texture ${name} not loaded!`);

    const sprite = new PIXI.Sprite(text);
    sprite.position = position;

    const container = new PIXI.Container();
    container.addChild(sprite);
    return container;
  }
}

class CacheGraphics extends PIXI.animate.Graphics {
  static cache: WeakMap<PIXI.animate.DrawCommands, PIXI.animate.Graphics> =
    new WeakMap();

  drawCommands(commands: PIXI.animate.DrawCommands): this {
    // Be sure nothing is called before drawCommands !
    if (CacheGraphics.cache.has(commands)) {
      const gr = CacheGraphics.cache.get(commands)!.clone();
      Object.setPrototypeOf(gr, CacheGraphics.prototype);
      return gr as this;
    }
    super.drawCommands(commands);
    CacheGraphics.cache.set(commands, this);
    return this;
  }
}
